'use client'
import { useEffect, useRef, useState } from 'react'
import { Dialog, DialogContent } from '@components/shared/dialog/Dialog'
import {
  PRIMARY_CHANGES_SUMMARY,
  TERMS_DOCS_URL,
  TERMS_UPDATE_COPY,
} from './termsUpdateAck.constants'

interface TermsUpdateAckDialogProps {
  open: boolean
  onGotIt: () => void
  error?: string | null
  isSaving?: boolean
}

export const TermsUpdateAckDialog = ({
  open,
  onGotIt,
  error = null,
  isSaving = false,
}: TermsUpdateAckDialogProps) => {
  const ctaRef = useRef<HTMLButtonElement>(null)
  const titleId = 'terms-update-ack-title'
  const [showPrimaryChanges, setShowPrimaryChanges] = useState(false)

  useEffect(() => {
    if (open) {
      const t = window.setTimeout(() => ctaRef.current?.focus(), 0)
      return () => window.clearTimeout(t)
    }
  }, [open])

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        isClosable={false}
        hideTitle
        title={TERMS_UPDATE_COPY.title}
        childrenClassName="!overflow-visible !max-h-none"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        className="!mt-0 !max-w-[420px] !rounded-[20px] !bg-[#1A1830] !px-6 !py-7 !text-[#F0EFFA] border border-[rgba(240,239,250,0.10)] shadow-[0_24px_80px_rgba(0,0,0,0.55)]"
        portalClassName="!items-center"
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className="flex flex-col"
        >
          <h2
            id={titleId}
            className="font-funnel font-semibold text-[22px] leading-[1.25] tracking-[-0.02em] text-[#F0EFFA] mb-3"
          >
            {TERMS_UPDATE_COPY.title}
          </h2>

          <p className="font-montserrat text-[15px] leading-[1.55] font-normal text-[#A09EB8] mb-[22px]">
            {TERMS_UPDATE_COPY.bodyBeforeTerms}
            <a
              href={TERMS_DOCS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#8B72FB] font-semibold underline underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#01FEBF] focus-visible:outline-offset-2 rounded-sm"
            >
              {TERMS_UPDATE_COPY.termsLinkPhrase}
            </a>
            {TERMS_UPDATE_COPY.bodyAfterTerms}
            <button
              type="button"
              onClick={() => setShowPrimaryChanges((prev) => !prev)}
              aria-expanded={showPrimaryChanges}
              className="text-[#8B72FB] font-semibold underline underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#01FEBF] focus-visible:outline-offset-2 rounded-sm"
            >
              {TERMS_UPDATE_COPY.hereLinkPhrase}
            </button>
            {TERMS_UPDATE_COPY.bodyAfterHere}
          </p>

          {showPrimaryChanges ? (
            <div
              className="mb-5 rounded-xl border border-[rgba(240,239,250,0.12)] bg-[#14122A] px-4 py-3 text-left"
              role="region"
              aria-label="Summary of primary changes"
            >
              <p className="font-montserrat text-sm font-semibold text-[#F0EFFA] mb-2">
                Primary changes
              </p>
              <ul className="list-disc pl-5 space-y-1.5 font-montserrat text-sm leading-[1.5] text-[#A09EB8]">
                {PRIMARY_CHANGES_SUMMARY.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {error ? (
            <p className="text-sm text-red-400 mb-3" role="alert">
              {error}
            </p>
          ) : null}

          <button
            ref={ctaRef}
            type="button"
            disabled={isSaving}
            onClick={onGotIt}
            className="w-full min-h-12 rounded-full bg-white text-[#0E0D1A] font-montserrat text-[15px] font-bold disabled:opacity-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#01FEBF] focus-visible:outline-offset-[3px]"
          >
            {isSaving ? TERMS_UPDATE_COPY.saving : TERMS_UPDATE_COPY.cta}
          </button>

          <p className="mt-3 text-center text-xs leading-[1.45] text-[#706E88] font-montserrat">
            {TERMS_UPDATE_COPY.microcopy}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}
